/* eslint-disable @typescript-eslint/ban-types */
import pick from 'ramda/src/pick'

type BuiltObject<
  Obj extends Record<string, unknown>,
  Rename extends Partial<Record<keyof Obj, string>>,
  Copy extends (keyof Obj)[] | boolean,
  Added extends Record<string, unknown>,
  Remove extends (keyof Obj)[],
  Default extends Partial<Obj>
> = Omit<
  {
    [K in keyof Rename as Rename[K] extends string ? Rename[K] : K]: Obj[K] extends undefined ? Default[K] : Obj[K]
  } & (Copy extends (keyof Obj)[]
    ? Pick<Obj, Copy[number]>
    : Copy extends true
    ? Obj
    : {}) &
    Added,
  Remove[number]
>

interface BuildObjectOptions<
  Obj extends Record<string, unknown>,
  Rename extends Partial<Record<keyof Obj, string>>,
  Copy extends (keyof Obj)[] | boolean,
  Added extends Record<string, unknown>,
  Remove extends (keyof Obj)[],
  Default extends Partial<Obj>

> {
  rename?: Rename
  copy?: Copy
  added?: Added
  remove?: Remove
  defaultValues?: Default
}

export const buildObject = <
  Obj extends Record<string, unknown>,
  Rename extends Partial<Record<keyof Obj, string>>,
  Copy extends (keyof Obj)[] | boolean,
  Added extends Record<string, unknown>,
  Remove extends (keyof Obj)[],
  Default extends Partial<Obj>
>(
  obj: Obj,
  {
    copy,
    rename,
    added,
    remove,
    defaultValues = {} as Default
  }: BuildObjectOptions<Obj, Rename, Copy, Added, Remove, Default>
): BuiltObject<Obj, Rename, Copy, Added, Remove, Default> => {
  const newObject: Record<string, unknown> = defaultValues

  if (copy) {
    Object.assign(newObject, copy === true ? obj : pick(copy, obj))
  }
  if (added) {
    Object.assign(newObject, added)
  }

  if (rename) {
    const renamed = Object.keys(rename).reduce(
      (acc, path) => ({ ...acc, [rename[path] as string]: obj[path] || defaultValues[path] }),
      {}
    )

    Object.assign(newObject, renamed)
  }

  if (remove) {
    remove.forEach(key => {
      delete newObject[key]
    })
  }

  return newObject as BuiltObject<Obj, Rename, Copy, Added, Remove, Default>
}
